import { useRef, useState } from 'react';
import { X, Tag, Camera, ImagePlus } from 'lucide-react';
import { useQuickList } from '@/hooks/useApi';
import CardImage from '@/components/CardImage';
import toast from 'react-hot-toast';

interface CollectionItem {
  id: string;
  quantity: number;
  condition: string;
  listing_image_urls?: string[];
  card: {
    name: string;
    set_name?: string;
    game?: string;
    image_url?: string;
    market_price?: number | null;
  };
}

interface Props {
  item: CollectionItem;
  onClose: () => void;
  onSuccess?: () => void;
}

const LISTING_TYPES = [
  { value: 'sale',  label: 'For Sale' },
  { value: 'trade', label: 'Trade' },
  { value: 'both',  label: 'Sale or Trade' },
] as const;

async function uploadImage(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append('images', file);
  try {
    const apiVersion = import.meta.env.VITE_API_VERSION || 'v1';
    const res = await fetch(`/api/${apiVersion}/uploads/images`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      body: formData,
    });
    const data = await res.json();
    return data.data?.urls?.[0] ?? null;
  } catch {
    return null;
  }
}

export default function QuickListingModal({ item, onClose, onSuccess }: Props) {
  const existingImageUrl = item.listing_image_urls?.[0] ?? item.card.image_url ?? null;

  const [price, setPrice]             = useState(
    item.card.market_price ? String(item.card.market_price.toFixed(2)) : ''
  );
  const [listingType, setListingType] = useState<string>('sale');
  const [quantity, setQuantity]       = useState(1);
  const [shippingPrice, setShippingPrice] = useState('');
  const [description, setDescription] = useState('');

  // Photo state — starts with the existing image, can be swapped
  const [previewUrl, setPreviewUrl]   = useState<string | null>(existingImageUrl);
  const [newFile, setNewFile]         = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { mutate: quickList, isLoading } = useQuickList();

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const priceNum = parseFloat(price);
    if (!price || isNaN(priceNum) || priceNum <= 0) {
      toast.error('Enter a valid price');
      return;
    }

    // Upload new photo if the user swapped it, otherwise keep existing
    let imageUrls: string[] | undefined;
    if (newFile) {
      const uploaded = await uploadImage(newFile);
      if (uploaded) {
        imageUrls = [uploaded];
      } else {
        toast.error('Photo upload failed — listing will use the existing image');
        imageUrls = existingImageUrl ? [existingImageUrl] : undefined;
      }
    } else if (existingImageUrl) {
      imageUrls = [existingImageUrl];
    }

    quickList(
      {
        id: item.id,
        data: {
          price:          priceNum,
          listing_type:   listingType,
          quantity:       Math.min(quantity, item.quantity),
          shipping_price: shippingPrice ? parseFloat(shippingPrice) : undefined,
          description:    description.trim() || undefined,
          image_urls:     imageUrls,
        },
      },
      {
        onSuccess: () => {
          toast.success(`${item.card.name} listed successfully!`);
          onSuccess?.();
          onClose();
        },
        onError: () => toast.error('Failed to create listing'),
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-zinc-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">List for Sale</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Card preview + photo */}
        <div className="flex items-center gap-4 px-5 py-4 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800">
          {/* Photo — click to change */}
          <div className="relative flex-shrink-0 group">
            <div className="h-20 w-14 rounded-xl overflow-hidden bg-gray-200 dark:bg-zinc-700 border-2 border-gray-200 dark:border-zinc-800 group-hover:border-brand-400 transition-colors">
              <CardImage
                src={previewUrl}
                alt={item.card.name}
                cardName={item.card.name}
                cardNumber={item.card?.card_number}
                game={item.card?.game}
                className="w-full h-full"
              />
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"
              title="Change photo"
            >
              <Camera className="h-5 w-5 text-white" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoChange}
              className="hidden"
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 dark:text-zinc-100 truncate">{item.card.name}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-500">{item.condition} · {item.quantity} in collection</p>
            {item.card.market_price && (
              <p className="text-xs text-emerald-600 font-medium mt-0.5">
                Market: ${item.card.market_price.toFixed(2)}
              </p>
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="mt-1 flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:text-brand-300 font-medium"
            >
              <ImagePlus className="h-3 w-3" />
              {previewUrl ? 'Change photo' : 'Add photo'}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-5 py-5 space-y-5">

            {/* Listing type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Type</label>
              <div className="grid grid-cols-3 gap-2">
                {LISTING_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setListingType(t.value)}
                    className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                      listingType === t.value
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white dark:bg-zinc-900 text-gray-700 dark:text-zinc-300 border-gray-200 dark:border-zinc-800 hover:border-brand-400'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Price *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 text-sm">$</span>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                  className="w-full pl-7 pr-4 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Quantity */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Quantity</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(item.quantity, Number(e.target.value))))}
                  min={1}
                  max={item.quantity}
                  className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
                <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1">Max {item.quantity}</p>
              </div>

              {/* Shipping */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Shipping</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 text-sm">$</span>
                  <input
                    type="number"
                    value={shippingPrice}
                    onChange={(e) => setShippingPrice(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-7 pr-3 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                Description <span className="font-normal text-gray-500 dark:text-zinc-500">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the card's condition, any extras, etc."
                rows={2}
                maxLength={500}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
              />
            </div>
          </div>

          <div className="px-5 py-4 border-t border-gray-200 dark:border-zinc-800 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Listing…' : 'Create Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
